/**
 * HotelBrandCard displays a brand card with images, logo, description, and links.
 * Handles rendering of brand images and card layout for desktop and mobile.
 */
"use client";

import { iconList } from "@/assets/icons";
import { Maybe, Scalars, UploadFile } from "@/types/generated";
import { useLocale } from "next-intl";
import Image from "next/image";
import React from "react";
import { SwiperSlide } from "swiper/react";
import { BannerSlider } from "../../hotel-details/banner-section/BannerSlider";
import { mapBrandInternalUrl } from "@/lib/utils";
import { RichtextRenderer } from "@/components/atoms/rich-text-render";
import ExpandableParagraphRichtext from "@/components/atoms/expandable-paragraph-richtext";
import { Link } from "@/components/atoms/link";
import { Button } from "@/components/atoms/button";

export interface HotelBrandCard {
  description: Maybe<Scalars["String"]["output"]>;
  brandMobileImage?: UploadFile[];
  brandDesktopImage?: UploadFile[];
  brandLogo?: UploadFile;
  brandUrl?: string;
  lessLinkText: string;
  moreLinkText: string;
  buttonText: string;
  target: string;
  is_cover: boolean;
}

const HotelBrandCard: React.FC<HotelBrandCard> = ({
  brandDesktopImage,
  brandMobileImage,
  brandLogo,
  description,
  lessLinkText,
  moreLinkText,
  buttonText,
  brandUrl,
  target,
  is_cover,
}) => {
  const brandLogoUrl = brandLogo?.cloudflare_url || "";
  const { WhiteCircleNext } = iconList;
  const locale = useLocale();

  return (
    <div className="flex flex-col rounded-xl bg-white lg:w-full lg:flex-row lg:rounded-none">
      {brandMobileImage && (
        <BannerSlider
          containerClassName="lg:hidden"
          paginationClassName={`left-1/2 ${brandMobileImage?.length > 1 ? "" : "hidden"}`}
        >
          {brandMobileImage?.map((item, index) => {
            const brandMobileImageKey = `brand-mobile-image-${index}`;
            return (
              <SwiperSlide key={brandMobileImageKey} className="rounded-xl">
                <div className="relative h-[229px] w-full">
                  <Image
                    src={item?.cloudflare_url || item?.url || ""}
                    alt={item?.alternativeText || ""}
                    aria-label={item?.caption || ""}
                    fill
                    className="rounded-t-xl object-cover"
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </BannerSlider>
      )}

      {brandDesktopImage && (
        <BannerSlider
          paginationClassName={`left-1/2 ${brandDesktopImage?.length > 1 ? "" : "hidden"}`}
          containerClassName="w-7/12 hidden lg:block z-10"
        >
          {brandDesktopImage?.map((item, index) => {
            const brandDesktopImageKey = `brand-desktop-image-${index}`;
            return (
              <SwiperSlide key={brandDesktopImageKey}>
                <div className="relative h-[570px]">
                  <Image
                    src={item?.cloudflare_url || item?.url || ""}
                    alt={item?.alternativeText || ""}
                    aria-label={item?.caption || ""}
                    fill
                    className="object-cover"
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </BannerSlider>
      )}

      <div className="z-10 flex flex-col items-center justify-center gap-4 rounded-b-xl bg-white p-4 lg:w-5/12 lg:rounded-none lg:px-6 lg:pt-0 lg:pb-0">
        {brandLogoUrl && (
          <h3
            className={`relative flex items-center justify-center ${
              is_cover
                ? "h-12 w-[200px] object-cover md:h-24 md:w-[350px]"
                : "h-16 w-[200px] object-contain md:h-30 md:w-[350px]"
            }`}
          >
            <Image
              src={brandLogoUrl}
              alt={brandLogo?.alternativeText || ""}
              aria-label={brandLogo?.caption || ""}
              fill
              className={is_cover ? "object-cover" : "object-contain"}
            />
          </h3>
        )}

        <ExpandableParagraphRichtext
          paragraph={description ?? ""}
          lessLinkText={lessLinkText}
          moreLinkText={moreLinkText}
          buttonClassName="md:text-sm text-xs"
          outerClassName="md:text-sm text-xs leading-4 lg:hidden"
          lineLimitMobile={2}
          defaultExpanded = {false}
          defaultShowButton = {false}
        />

        {description && (
          <div className="hidden lg:block">
            <RichtextRenderer
              customStyle={{
                p: {
                  fontSize: "16px",
                  color: "#2c2c2c",
                },
                span: {
                  fontSize: "var(--text-base) !important",
                },
              }}
              content={description}
            />
          </div>
        )}

        <Link
          target={target as "_self" | "_blank" | "_parent" | "_top"}
          href={`/${locale}${mapBrandInternalUrl(brandUrl || "")}`}
        >
          <Button
            size="lg"
            className="flex w-full justify-between md:text-base lg:h-14 lg:w-[300px]"
          >
            {buttonText ?? "ホテル一覧へ"}
            <div className="">
              <WhiteCircleNext />
            </div>
          </Button>
        </Link>
      </div>
    </div>
  );
};

export { HotelBrandCard };
